import React, { useState } from "react";
import "./PlatformHeroMark.css";
import { resolvePlatformHeroImage, resolvePlatformHeroKey } from "../utils/platformBranding";

/**
 * One bounded, aspect-preserving frame for a platform's canonical hero
 * artwork, used wherever a platform is named — the campaign requirements
 * list, the issued-link rows, the connection detail, and the client's own
 * connection cards.
 *
 * Three properties it exists to guarantee:
 *
 *  1. ONE ASSET MAPPING. The image comes from `resolvePlatformHeroImage`, the
 *     same canonical resolver Platform Analytics uses. No component picks its
 *     own file, and no hardcoded URL appears anywhere.
 *
 *  2. UNKNOWN FAILS GRACEFULLY, NOT WRONGLY. A platform the mapping does not
 *     know resolves to the `unknown` key; rather than show unrelated artwork,
 *     the frame renders a monogram of the platform's own name. A file that
 *     404s at runtime degrades to the same monogram.
 *
 *  3. NO LAYOUT SHIFT. The frame is a fixed square at each size and the image
 *     is `object-fit: cover` inside it, so the row's height is decided before
 *     the image loads and never moves when it arrives.
 *
 * The image is decorative: the platform's name is always rendered beside it as
 * real text, so the frame carries `alt=""` and an `aria-hidden` monogram. The
 * accessible name of the row comes from that text, not from this.
 */
export default function PlatformHeroMark({ heroKey, label = "", size = "md", className = "" }) {
  const [failed, setFailed] = useState(false);

  const resolvedKey = resolvePlatformHeroKey(heroKey || label);
  // "unknown" is only honest artwork when the caller actually asked for it.
  const isUnknown = resolvedKey === "unknown" && String(heroKey || "").trim().toLowerCase() !== "unknown";
  const src = isUnknown || failed ? null : resolvePlatformHeroImage(heroKey || label);

  const monogram = String(label || heroKey || "?")
    .trim()
    .split(/[\s_-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0])
    .join("")
    .toUpperCase() || "?";

  const classes = ["cphere-platform-hero-mark", `cphere-platform-hero-mark--${size}`, className]
    .filter(Boolean)
    .join(" ");

  if (!src) {
    return (
      <span className={classes} data-platform-hero={resolvedKey} data-fallback="true" aria-hidden="true">
        <span className="cphere-platform-hero-mark-monogram">{monogram}</span>
      </span>
    );
  }

  return (
    <span className={classes} data-platform-hero={resolvedKey} data-fallback="false">
      <img
        className="cphere-platform-hero-mark-image"
        src={src}
        alt=""
        aria-hidden="true"
        loading="lazy"
        decoding="async"
        draggable="false"
        onError={() => setFailed(true)}
      />
    </span>
  );
}
